import { client } from '@api/rest/client';
import { toastErrorTranslated } from '@helpers/toast';
import type { TInbox } from '@interfaces/TChat';
import { useCallback, useEffect, useRef, useState } from 'react';

export enum EInboxLoadStatus {
  Loading = 'loading',
  Loaded = 'loaded',
  Failed = 'failed',
}

export type TInboxLoadState =
  | { status: EInboxLoadStatus.Loading }
  | { status: EInboxLoadStatus.Loaded; inboxes: TInbox[] }
  | { status: EInboxLoadStatus.Failed };

/**
 * Loads the company inboxes the first time `isEnabled` is true. The hook keeps
 * them while the caller is mounted, so the inbox field loads them only once.
 * The list stays local, because the shared inbox fetch shows a full-screen
 * loading state over the open modal.
 */
export const useInviteInboxes = (isEnabled: boolean) => {
  const [loadState, setLoadState] = useState<TInboxLoadState>({
    status: EInboxLoadStatus.Loading,
  });
  const [loadAttempt, setLoadAttempt] = useState(0);
  const hasLoadedRef = useRef(false);

  // * loadAttempt is a dependency only so that a retry starts a new load.
  useEffect(() => {
    if (!isEnabled || hasLoadedRef.current) {
      return;
    }

    const request = { isCancelled: false };
    setLoadState({ status: EInboxLoadStatus.Loading });

    client.inbox
      .getAllInboxesOfCompany()
      .then(({ data }) => {
        if (request.isCancelled) {
          return;
        }

        Object.assign(hasLoadedRef, { current: true });
        setLoadState({ status: EInboxLoadStatus.Loaded, inboxes: data });
      })
      .catch((error: unknown) => {
        if (request.isCancelled) {
          return;
        }

        toastErrorTranslated(error);
        setLoadState({ status: EInboxLoadStatus.Failed });
      });

    return () => {
      Object.assign(request, { isCancelled: true });
    };
  }, [isEnabled, loadAttempt]);

  const retry = useCallback(() => {
    setLoadAttempt((currentAttempt) => currentAttempt + 1);
  }, []);

  return { loadState, retry };
};
